import { createContext, useState, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';

// color design tokens export
export const tokens = (mode) => ({
	...(mode === 'dark'
		? {
				white: '#000000',
				black: '#fff',
				grey: {
					100: '#141414',
					200: '#292929',
					300: '#3d3d3d',
					400: '#525252',
					500: '#666666',
					600: '#858585',
					700: '#a3a3a3',
					800: '#bdbdbd',
					900: '#e0e0e0',
				},
				primary: {
					100: '#162A5C', // Darkest light shade
					200: '#1A3A7A', // Darker still
					300: '#1F4F99', // Darker shade
					400: '#2A6BCC', // Base light mode color
					500: '#4D8CFF', // Medium light
					600: '#80B0FF', // Light
					700: '#B3D6FF', // Lighter shade
					800: '#E3EFFF', // Lightest shade
				},
				greenAccent: {
					100: '#dbf5ee',
					200: '#b7ebde',
					300: '#94e2cd',
					400: '#70d8bd',
					500: '#4cceac',
					600: '#2e7c67',
					700: '#3da58a',
					800: '#1e5245',
					900: '#0f2922',
				},
				redAccent: {
					900: '#FAD3D4', // Very light (same as light mode)
					800: '#F5A7A9', // Light tint (same as light mode)
					700: '#F07B7E', // Medium tint (same as light mode)
					600: '#EB4F53', // Slightly darker (same as light mode)
					500: '#CE161E', // Main color (same as light mode)
					400: '#B21318', // Darker shade (darker than light mode)
					300: '#970F14', // Darker (darker than light mode)
					200: '#7D0C10', // Even darker (darker than light mode)
					100: '#64090C', // Very dark (darker than light mode)
				},
				blueAccent: {
					50: '#273240',
					100: '#0a4356',
					400: '#008cd5',
					300: '#0a4356',
					200: '#003f7d',
					600: '#2c3e4e',
				},
				yellowAccent: {
					100: '#fff4eb', // Very light (same as light mode)
					200: '#ffebd6', // Light tint (same as light mode)
					300: '#ffdfc6', // Main color (same as light mode)
					400: '#ffcd9d', // Slightly darker
					500: '#ffb74d', // Darker shade
					// Darker
					600: '#cc8f00', // Even darker
					700: '#996f00', // Deep shade
					800: '#664f00', // Very dark
				},
			}
		: {
				white: '#ffffff',
				black: '#000000',
				grey: {
					100: '#e0e0e0',
					200: '#bdbdbd',
					300: '#a3a3a3',
					400: '#858585',
					500: '#666666',
					600: '#525252',
					700: '#3d3d3d',
					800: '#ededed',
					900: '#141414',
				},
				primary: {
					100: '#E3EFFF', // Lightest shade
					200: '#B3D6FF', // Light
					300: '#80B0FF', // Medium light
					400: '#4D8CFF', // Base light mode color
					500: '#2A6BCC', // Slightly darker
					600: '#1F4F99', // Darker shade
					700: '#1A3A7A', // Darker still
					800: '#162A5C', // Darkest light shade
				},
				greenAccent: {
					100: '#0f2922',
					200: '#1e5245',
					300: '#2e7c67',
					400: '#3da58a',
					500: '#4cceac',
					600: '#70d8bd',
					700: '#94e2cd',
					800: '#b7ebde',
					900: '#dbf5ee',
				},
				redAccent: {
					100: '#FAD3D4', // Very light
					200: '#F5A7A9', // Light tint
					300: '#F07B7E', // Medium tint
					400: '#EB4F53', // Slightly darker
					500: '#CE161E', // Main color
					600: '#B21318', // Darker tint
					700: '#970F14', // Darker
					800: '#7D0C10', // Even darker
					900: '#64090C', // Very dark
				},
				blueAccent: {
					50: '#BFE0FF',
					100: '#55ddee',
					200: '#0088de',
					300: '#55ddee',
					400: '#0083FC',
					600: '#76AACA',
				},
				yellowAccent: {
					100: '#fff4eb', // Very light
					200: '#ffebd6', // Light tint
					300: '#ffdfc6', // Main color
					400: '#ffd1a1', // Slightly darker
					500: '#ffbc7a', // Darker tint
					600: '#ffa654', // Darker
					700: '#ff8f1f', // Even darker
					800: '#cc7400', // Deep shade
					900: '#995600', // Very dark
				},
			}),
});

// mui theme settings
export const themeSettings = (mode) => {
	const colors = tokens(mode);
	return {
		components: {
			// Name of the component
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						// Some CSS
						color: colors.black,
						minWidth: '0px',
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: '20px',
						'&:disabled': {
							cursor: 'not-allowed',
							pointerEvents: 'all !important',
						},
					},

					contained: {
						background: `linear-gradient(to right, #332E2F  0%, ${colors.redAccent[500]}  80%)`,
						color: 'white',
						'&:disabled': {
							background: colors.grey[800],
							color: colors.grey[200],
						},
					},
					containedError: {
						background: '#FF3131',
						color: 'white',
					},

					outlined: {
						border: `1px solid ${colors.redAccent[500]}`,
						color: `${colors.redAccent[500]}`,
						'&:hover': {
							border: `1px solid ${colors.primary[600]}`,
							color: `${colors.primary[600]}`,
						},
					},
					outlinedError: {
						border: `1px solid #FF3131`,
						'&:hover': {
							border: `1px solid #ff3190`,
							color: ' #ff3190',
						},
						color: '#FF3131',
					},
					text: {
						color: `${colors.redAccent[500]}`,
					},
				},
			},

			MuiDateCalendar: {
				styleOverrides: {
					root: {
						backgroundColor: `${colors.grey[100]}`,
						borderRadius: '20px',
					},
				},
			},
			MuiPickersDay: {
				styleOverrides: {
					root: {
						backgroundColor: `${colors.grey[100]}`,
					},
					'&.Mui-selected': {
						backgroundColor: `${colors.blueAccent[500]}`,
						color: `${colors.white}`,
					},
				},
			},
			MuiPickersLayout: {
				styleOverrides: {
					root: {
						backgroundColor: 'transparent !important',
					},
				},
			},
		},
		palette: {
			error: {
				main: '#FF3131',
			},
			mode: mode,
			...(mode === 'dark'
				? {
						// palette values for dark mode
						primary: {
							main: colors.redAccent[500],
						},
						secondary: {
							main: colors.greenAccent[500],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[500],
							light: colors.grey[100],
						},
						background: {
							default: colors.primary[500],
						},
					}
				: {
						// palette values for light mode
						primary: {
							main: colors.redAccent[500],
						},
						secondary: {
							main: colors.greenAccent[500],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[500],
							light: colors.grey[100],
						},
						background: {
							default: colors.grey[800],
						},
					}),
		},
		typography: {
			fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
			fontSize: 12,
			h1: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 40,
			},
			h2: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 32,
			},
			h3: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 24,
			},
			h4: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 20,
			},
			h5: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 16,
			},
			h6: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 14,
			},
			caption: {
				fontFamily: ['Tenor Sans', 'sans-serif'].join(','),
				fontSize: 10,
			},
		},
	};
};

// context for color mode
export const ColorModeContext = createContext({
	toggleColorMode: () => {},
});

export const useMode = () => {
	const savedMode = localStorage.getItem('colorMode');

	const startMode = savedMode ? JSON.parse(savedMode) : 'light';

	const [mode, setMode] = useState(startMode);

	const colorMode = useMemo(
		() => ({
			toggleColorMode: () => {
				setMode((prev) => {
					const newMode = prev === 'light' ? 'dark' : 'light';
					localStorage.setItem('colorMode', JSON.stringify(newMode));
					return newMode;
				});
			},
		}),
		[]
	);

	const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
	return [theme, colorMode];
};
